import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, X } from "lucide-react";
import { getItem, setItem, onChange } from "../lib/safeStorage";

const STORAGE_KEY = "zen_first_loc_picked_v1";
const SELECTED_KEY = "zen_selected_store_v1";

// Two physical NY OCM-licensed flagships — keep in sync with backend
// /data/locations.py. Hard-coded here so the modal renders instantly without
// waiting on /api/locations.
const STORES = [
    {
        slug: "queens-long-island",
        short: "Queens & Long Island",
        tagline: "272-06 Union Tpke · serving 50+ Nassau & Queens neighborhoods",
        image: "https://customer-assets.emergentagent.com/job_zenzest-near-me/artifacts/xkyk453i_ZenZest%20Pic.jpeg",
    },
    {
        slug: "staten-island",
        short: "Staten Island",
        tagline: "2343 Forest Ave · borough-wide delivery + Bayonne/Linden NJ",
        image: "https://customer-assets.emergentagent.com/job_zenzest-near-me/artifacts/xkyk453i_ZenZest%20Pic.jpeg",
    },
];

export default function LocationPickerModal() {
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const picked = getItem(STORAGE_KEY);
        if (!picked) {
            const t = setTimeout(() => setOpen(true), 250);
            return () => clearTimeout(t);
        }
    }, []);

    // Cross-tab sync — if another tab picks a store, close the modal here.
    useEffect(() => {
        return onChange(STORAGE_KEY, (newVal) => {
            if (newVal) setOpen(false);
        });
    }, []);

    const pick = (slug) => {
        setItem(STORAGE_KEY, "1");
        setItem(SELECTED_KEY, slug);
        setOpen(false);
        navigate(`/menu/${slug}`);
    };

    const dismiss = () => {
        setItem(STORAGE_KEY, "1");
        setOpen(false);
    };

    if (!open) return null;

    return (
        <div
            data-testid="location-picker-overlay"
            role="dialog"
            aria-modal="true"
            aria-label="Pick your store"
            className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-zen-ink/70 backdrop-blur-sm"
        >
            <div className="relative bg-zen-bg max-w-3xl w-full border border-zen-ink/15 shadow-[0_40px_120px_rgba(31,36,24,0.45)]">
                <button
                    onClick={dismiss}
                    data-testid="location-picker-dismiss"
                    aria-label="Skip and browse all stores"
                    className="absolute top-3 right-3 p-2 text-zen-ink-soft hover:text-zen-ink"
                >
                    <X size={18} />
                </button>
                <div className="p-6 sm:p-10">
                    <p className="text-[11px] uppercase tracking-[0.35em] text-zen-olive mb-3 inline-flex items-center gap-2">
                        <MapPin size={12} /> Pick your store
                    </p>
                    <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-zen-ink leading-[1.05] mb-2">
                        Where are you shopping today?
                    </h2>
                    <p className="text-zen-ink-soft text-sm sm:text-base mb-7 max-w-2xl">
                        We&apos;ll show pricing, deals, and delivery zones for the
                        store you pick. You can switch any time from the
                        header.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {STORES.map((s) => (
                            <button
                                key={s.slug}
                                onClick={() => pick(s.slug)}
                                data-testid={`location-picker-${s.slug}`}
                                className="group text-left bg-zen-surface border border-zen-ink/10 hover:border-zen-olive hover:bg-zen-bg transition-all duration-300 overflow-hidden"
                            >
                                <div className="aspect-[5/3] overflow-hidden bg-zen-elevated">
                                    <img
                                        src={s.image}
                                        alt={s.short}
                                        loading="lazy"
                                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                    />
                                </div>
                                <div className="p-5">
                                    <p className="text-[10px] uppercase tracking-[0.28em] text-zen-olive mb-1.5">
                                        ZenZest
                                    </p>
                                    <h3 className="font-serif text-2xl text-zen-ink font-light leading-tight mb-2">
                                        {s.short}
                                    </h3>
                                    <p className="text-xs text-zen-ink-soft leading-relaxed line-clamp-2">
                                        {s.tagline}
                                    </p>
                                </div>
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={dismiss}
                        data-testid="location-picker-skip"
                        className="mt-6 text-[11px] uppercase tracking-[0.25em] text-zen-ink-soft hover:text-zen-ink border-b border-zen-ink/20 pb-1"
                    >
                        Skip — let me browse the site first
                    </button>
                </div>
            </div>
        </div>
    );
}
